import { styled, alpha } from '@mui/material/styles';
import { Box, Button, TextField } from '@mui/material';

export const MainContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  // flex:1 + minHeight:0 bounds this to the space left by the modal header/progress
  // bar so the inner columns scroll instead of the content being pushed off-screen
  // and clipped at higher zoom levels (height:100% grew to content size).
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  backgroundColor: theme.palette.background.default,
  gap: '32px',
}));

export const LeftColumn = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: '24px',
  overflow: 'auto',
  backgroundColor: theme.palette.background.default,
}));

export const RightColumn = styled(Box)(({ theme }) => ({
  width: 400,
  padding: '24px',
  backgroundColor:
    theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.grey[50],
  borderLeft: theme.palette.mode === 'dark' ? `1px solid ${theme.palette.divider}` : 'none',
  overflow: 'auto',
}));

export const ConfigToggleBox = styled(Box)<{ isActive: boolean }>(({ theme, isActive }) => ({
  padding: theme.spacing(1),
  borderRadius: theme.spacing(1.5),
  border: '1px solid',
  borderColor: isActive ? theme.palette.success.main : theme.palette.divider,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, isActive ? 0.06 : 0.04)
      : theme.palette.background.paper,
  transition: 'all 0.15s ease',
  minHeight: 60,
  '&:hover': {
    borderColor: isActive ? theme.palette.success.main : theme.palette.text.secondary,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, isActive ? 0.1 : 0.06)
        : theme.palette.background.paper,
    transform: 'translateY(-1px)',
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 2px 8px rgba(0, 0, 0, 0.35)'
        : '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
}));

export const PaymentToggleBox = styled(Box)<{ isActive: boolean }>(({ theme, isActive }) => ({
  padding: theme.spacing(1),
  borderRadius: theme.spacing(1.5),
  border: '1px solid',
  borderColor: isActive ? theme.palette.success.main : theme.palette.divider,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, isActive ? 0.06 : 0.04)
      : theme.palette.background.paper,
  transition: 'all 0.15s ease',
  minHeight: 60,
  '&:hover': {
    borderColor: isActive ? theme.palette.success.main : theme.palette.text.secondary,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, isActive ? 0.1 : 0.06)
        : theme.palette.background.paper,
    transform: 'translateY(-1px)',
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 2px 8px rgba(0, 0, 0, 0.35)'
        : '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
}));

export const DiscountBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.05)
      : theme.palette.background.paper,
  borderRadius: theme.spacing(1),
  border: '1px solid',
  borderColor: theme.palette.divider,
}));

export const PaymentMethodButton = styled(Button)<{ isSelected: boolean }>(
  ({ theme, isSelected }) => ({
    padding: theme.spacing(1),
    flexDirection: 'column',
    height: 'auto',
    minHeight: 60,
    backgroundColor: isSelected ? theme.palette.primary.main : 'transparent',
    borderColor: isSelected ? theme.palette.primary.main : theme.palette.divider,
    color: isSelected ? theme.palette.primary.contrastText : theme.palette.text.primary,
    borderRadius: theme.spacing(1.5),
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: isSelected ? theme.palette.primary.dark : theme.palette.action.hover,
      borderColor: isSelected ? theme.palette.primary.dark : theme.palette.text.secondary,
      transform: 'translateY(-1px)',
      boxShadow:
        theme.palette.mode === 'dark'
          ? '0 2px 8px rgba(0, 0, 0, 0.35)'
          : '0 2px 8px rgba(0, 0, 0, 0.1)',
    },
    '&.MuiButton-contained': {
      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
    },
  }),
);

export const PaymentMethodIconBox = styled(Box)<{ isSelected: boolean }>(
  ({ theme, isSelected }) => ({
    padding: '4px',
    borderRadius: '50%',
    backgroundColor: isSelected
      ? alpha(theme.palette.common.white, 0.18)
      : alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.06),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),
);

export const InfoMethodBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1),
}));

export const ChangeBadgeField = styled(TextField)<{ isPartial?: boolean; hasChange?: boolean }>(
  ({ theme, isPartial, hasChange }) => ({
    '& .MuiInputBase-input': {
      backgroundColor: theme.palette.background.paper,
      color: isPartial
        ? theme.palette.mode === 'dark'
          ? theme.palette.warning.light
          : theme.palette.warning.dark
        : hasChange
          ? theme.palette.mode === 'dark'
            ? theme.palette.success.light
            : theme.palette.success.dark
          : theme.palette.mode === 'dark'
            ? theme.palette.error.light
            : theme.palette.error.dark,
    },
  }),
);

export const ProductCardBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  borderBottom: '1px solid',
  borderColor: theme.palette.divider,
}));

export const ValidationList = styled('ul')({
  margin: 0,
  paddingLeft: '16px',
});

export const NavigationBox = styled(Box)({
  space: 2,
});

export const BackButton = styled(Button)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  bgcolor: theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[100],
  color: theme.palette.text.primary,
  borderColor: theme.palette.divider,
  '&:hover': {
    bgcolor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.12)
        : theme.palette.grey[200],
    borderColor: theme.palette.divider,
  },
  transition: 'all 0.2s',
}));

const salesCyanGreenGradient = 'linear-gradient(to right, rgb(6, 182, 212), rgb(16, 185, 129))';
const salesCyanGreenGradientHover =
  'linear-gradient(to right, rgb(8, 145, 178), rgb(14, 159, 104))';

export const ContinueButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  background: salesCyanGreenGradient,
  color: 'white',
  '&:hover': {
    background: salesCyanGreenGradientHover,
  },
  transition: 'all 0.2s',
  '&:disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
    background: theme.palette.action.disabledBackground,
  },
}));

/** Configuración → productos: mismo degradé cyan–verde, ancho completo en uso */
export const SubStepGradientContinueButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(1.25, 2),
  borderRadius: theme.spacing(1),
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  background: salesCyanGreenGradient,
  color: 'white',
  '&:hover': {
    background: salesCyanGreenGradientHover,
  },
  transition: 'all 0.2s',
  '&:disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
    background: theme.palette.action.disabledBackground,
  },
}));

/** Productos → pagos: borde verde, fondo blanco; hover = tinte verde (antes era el estado normal) */
export const SubStepGhostContinueButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(1.25, 2),
  borderRadius: theme.spacing(1),
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  border: '1px solid',
  borderColor: theme.palette.mode === 'dark' ? '#34d399' : '#10b981',
  color: theme.palette.mode === 'dark' ? '#6ee7b7' : '#059669',
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#ffffff',
  boxShadow: 'none',
  transition: 'background-color 0.2s ease, border-color 0.2s ease',
  '&:hover': {
    backgroundColor: alpha('#10b981', theme.palette.mode === 'dark' ? 0.14 : 0.08),
    borderColor: theme.palette.mode === 'dark' ? '#6ee7b7' : '#047857',
  },
  '&:disabled': {
    borderColor: theme.palette.divider,
    color: theme.palette.action.disabled,
    backgroundColor: theme.palette.action.disabledBackground,
  },
}));

export const ModalCancelButton = styled(Button)(({ theme }) => ({
  boxShadow: 'none',
  backgroundColor: 'transparent',
  color: theme.palette.text.secondary,
  border: 'none',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    boxShadow: 'none',
  },
}));

export const ModalActionButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(to right, #06b6d4, #10b981)',
  color: '#ffffff',
  boxShadow: 'none',
  '&:hover': {
    background: 'linear-gradient(to right, #0891b2, #059669)',
    boxShadow: 'none',
  },
  '&:disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
    background: theme.palette.action.disabledBackground,
  },
}));

export const InfoBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
}));

export const InfoContent = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.18 : 0.12),
  border: `1px solid ${theme.palette.success.main}`,
  borderRadius: '8px',
  padding: '8px 16px',
  width: '100%',
}));

export const PricingLineCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.25),
  borderRadius: theme.spacing(1.25),
  border: '1px solid',
  borderColor: theme.palette.divider,
  backgroundColor: theme.palette.background.paper,
  marginBottom: theme.spacing(1),
}));

export const PricingWaterfall = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.25),
  padding: theme.spacing(0.75),
  borderRadius: theme.spacing(0.75),
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.04)
      : theme.palette.grey[50],
}));

export const PricingWaterfallRow = styled(Box)<{ tone?: 'markup' | 'discount' | 'final' }>(
  ({ theme, tone }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    paddingTop: tone === 'final' ? theme.spacing(0.5) : 0,
    marginTop: tone === 'final' ? theme.spacing(0.25) : 0,
    borderTop: tone === 'final' ? `1px dashed ${theme.palette.divider}` : 'none',
  }),
);

export const AdjustmentPanel = styled(Box)<{ tone: 'markup' | 'discount' }>(({ theme, tone }) => ({
  padding: theme.spacing(0.75),
  borderRadius: theme.spacing(0.75),
  border: '1px solid',
  borderColor:
    tone === 'markup'
      ? alpha(theme.palette.success.main, 0.3)
      : alpha(theme.palette.error.main, 0.3),
  backgroundColor:
    tone === 'markup'
      ? alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.06 : 0.04)
      : alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.06 : 0.03),
}));

export const ToolbarPanel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  borderRadius: theme.spacing(1),
  border: '1px solid',
  borderColor: theme.palette.divider,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.03)
      : theme.palette.grey[50],
}));
